import { fetcher } from "@certn/api";
import { useCallback, useContext, useEffect, useRef } from "react";

import { useApplicationFormContext } from "../../../ApplicationFormContext";
import { OneIdLayoutContext } from "../../../layouts/OneIdLayoutContext";
import { ONE_ID_COMPLETION_TIMESTAMP_KEY } from "../../constants";

export function useFallbackHandler() {
  const { fallbackPollTimeout, onFallbackTriggered } =
    useContext(OneIdLayoutContext);
  const { caseId } = useApplicationFormContext();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const callFallbackTimeout = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    const now = new Date().getTime();
    const completionTimestampString = localStorage.getItem(
      ONE_ID_COMPLETION_TIMESTAMP_KEY
    );
    if (!completionTimestampString) return;
    const completionTimestamp = new Date(completionTimestampString).getTime();
    if (isNaN(completionTimestamp)) return;
    const msPast = Math.max(now - completionTimestamp, 0);
    if (msPast > fallbackPollTimeout) {
      // Notify that fallback is about to be triggered
      // to avoid race condition
      onFallbackTriggered?.();
      try {
        await fetcher.submit(
          `/api/internal/applicant/case/${caseId}/one-id/fallback-to-secondary-id/`
        );
        await fetcher.request(`/api/internal/applicant/case/${caseId}/`);
      } catch (error) {
        console.error("Error in fallback handler:", error);
      }
    } else {
      timeoutRef.current = setTimeout(
        callFallbackTimeout,
        fallbackPollTimeout - msPast
      ) as unknown as NodeJS.Timeout;
    }
  }, [caseId, fallbackPollTimeout, onFallbackTriggered]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const completionTimestampString = localStorage.getItem(
      ONE_ID_COMPLETION_TIMESTAMP_KEY
    );
    if (completionTimestampString && !timeoutRef.current) {
      callFallbackTimeout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { callFallbackTimeout };
}
